/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import PropTypes from 'prop-types';

import { classNames } from '../../../utils';
import Strings from '../../../Strings';

/**
 * defines a table column
 * @param rowId - filled by <Table>
 * @param className - an optional className
 * @param children - not used
 * @param data - row data
 * @param value - a string or a react component to render as value
 * @param sortBy - sort key for the column ex. 'project.meta.code'
 * @param onClick - onClick fired when the table data cell of that column is selected
 */

export const Column = ({ rowId, className = '', data, value, onClick }) => {
  const cls = ['column', typeof className === 'function' ? className(data) : className].join(' ');
  if (className === 'study-name') {
    return (
      <td>
        <div className={cls} onClick={onClick}>
          {value(data, rowId)}
        </div>
      </td>
    );
  }

  // remove from words clickable and fix
  const clsFiltered = cls
    .split(' ')
    .filter(item => item !== 'clickable' && item !== 'fix' && item !== 'column')
    .join(' ');

  return (
    <td className={cls} onClick={onClick} data-testid={`column[${clsFiltered}][${rowId}]`}>
      {value(data, rowId)}
    </td>
  );
};

Column.propTypes = {
  className: PropTypes.string,
  data: PropTypes.any,
  onClick: PropTypes.func,
  rowId: PropTypes.string,
  value: PropTypes.func,
};

/**
 * renders standard header
 */

export const HeaderCell = ({ label, className, order, onClick, sortKey }) => {
  // Map 'ascending'/'descending' to 'asc'/'desc' for CSS classes
  const orderClass = order === 'ascending' ? 'asc' : order === 'descending' ? 'desc' : '';
  
  return (
    <th onClick={sortKey && onClick} className={['column', className, orderClass, sortKey ? 'sortable' : ''].filter(Boolean).join(' ')}>
      <span>
        <a>{label}</a>
      </span>
      {sortKey && <span className={`sort-icon ${orderClass}`} />}
    </th>
  );
};

HeaderCell.propTypes = {
  label: PropTypes.node,
  className: PropTypes.string,
  order: PropTypes.any,
  onClick: PropTypes.func,
  sortKey: PropTypes.string,
};

/**
 * header checkbox of the optional selection column
 * indeterminate can only be set as a DOM property, not as an attribute
 */
export const SelectAllCheckbox = ({ checked, indeterminate, disabled, title, onChange }) => {
  const ref = React.useRef();
  React.useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      type="checkbox"
      className="item-checkbox"
      ref={ref}
      checked={checked}
      disabled={disabled}
      title={title}
      aria-label={Strings.selectAll}
      onChange={e => onChange(e.target.checked)}
      data-testid="table-select-all"
    />
  );
};

SelectAllCheckbox.propTypes = {
  checked: PropTypes.bool,
  indeterminate: PropTypes.bool,
  disabled: PropTypes.bool,
  title: PropTypes.string,
  onChange: PropTypes.func,
};

export const selectionPropType = PropTypes.shape({
  isSelected: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  allSelected: PropTypes.bool,
  someSelected: PropTypes.bool,
  onToggleAll: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
});

const SelectionHeaderCell = ({ selection }) => (
  <th className="checkbox-column">
    <SelectAllCheckbox
      checked={!!selection.allSelected}
      indeterminate={!selection.allSelected && !!selection.someSelected}
      disabled={selection.disabled}
      title={selection.disabled ? selection.disabledTooltip : undefined}
      onChange={selection.onToggleAll}
    />
  </th>
);

SelectionHeaderCell.propTypes = { selection: selectionPropType.isRequired };

// clicks in the cell must not reach the row, which has its own onClick (e.g. navigation)
const SelectionCell = ({ selection, rowId, data }) => (
  <td className="checkbox-column" onClick={e => e.stopPropagation()}>
    <input
      type="checkbox"
      className="item-checkbox"
      checked={!!selection.isSelected(data)}
      onChange={e => selection.onToggle(data, e.target.checked)}
      data-testid={`table-select-row[${rowId}]`}
    />
  </td>
);

SelectionCell.propTypes = {
  selection: selectionPropType.isRequired,
  rowId: PropTypes.string,
  data: PropTypes.any,
};

export const HeaderRow = ({ order, columns, onClick, selection }) => {
  return (
    <tr className="table-row">
      {selection && <SelectionHeaderCell selection={selection} />}
      {columns &&
        columns.map(c => (
          <HeaderCell
            key={c.key}
            className={c.key}
            sortKey={c.sortKey}
            order={c.key === order.key ? order.direction : undefined}
            label={c.title}
            onClick={() => onClick(c.key)}
          />
        ))}
    </tr>
  );
};

HeaderRow.propTypes = {
  order: PropTypes.any,
  columns: PropTypes.array,
  onClick: PropTypes.func,
  selection: selectionPropType,
};

/**
 * creates a table row component basing on child column definition components
 * @param columns <Table>'s <Column ...> elements
 */

export const DataRowTemplate = columns => ({
  rowId,
  data,
  selected,
  onClick,
  onDoubleClick,
  toolTip,
  rowClass,
  isLoading,
  uiClass,
  tabIndex,
  buttons,
  excludedColumns,
  selection,
}) => {
  try {
    const trProps = {};
    if (toolTip && toolTip.idField in data) {
      trProps['data-tip'] = true;
      trProps['data-tooltip-id'] = toolTip.dataFor(data[toolTip.idField]);
    }

    if (tabIndex) {
      trProps.tabIndex = 0;
    }
    const classes = {
      'table-row': true,
      selected,
      unread: !data.read,
    };

    if (rowClass) {
      classes[rowClass(data)] = true;
    }

    return (
      <tr
        className={`${classNames(classes)} ${uiClass} ${buttons && buttons.length > 0 ? 'with-buttons' : ''}`}
        onClick={() => (onClick != undefined ? onClick(rowId) : undefined)}
        onDoubleClick={() => (onDoubleClick != undefined ? onDoubleClick(rowId) : undefined)}
        onKeyDown={key => {
          if (key.which === 13) {
            onClick(rowId);
          }
        }}
        {...trProps}
      >
        {selection && <SelectionCell selection={selection} rowId={rowId} data={data} />}
        {columns &&
          columns
            .filter(e => !excludedColumns.includes(e.key))
            .map((c, index) => {
              let className;
              if (c.props.className) {
                if (typeof c.props.className === 'function') {
                  className = d => [c.key, c.props.className(d)].join(' ');
                } else {
                  className = [c.key, c.props.className].join(' ');
                }
              } else {
                className = c.key;
              }

              const ret = React.cloneElement(c, {
                rowId,
                data,
                className,
                key: index,
              });

              return ret;
            })}
        {buttons && buttons.length > 0 && (
          <td className="sticky-right">
            <div className="buttons-wrapper">
              <div className="buttons-background-gradient" />
              <div className="buttons-background">
                <div className="buttons">
                  {buttons.map((b, idx) => {
                    const icon = typeof b.icon === 'function' ? b.icon(rowId, data) : b.icon;
                    const disabled =
                      (typeof b.disabled === 'function' && b.disabled(rowId, data)) ||
                      (typeof b.disabled == 'boolean' && b.disabled);
                    const highlighted =
                      (typeof b.highlighted === 'function' && b.highlighted(rowId, data) === true) ||
                      (typeof b.highlighted == 'boolean' && b.highlighted);
                    const text = typeof b.text === 'function' ? b.text(rowId, data) : b.text;
                    const disabledTooltip =
                      typeof b.disabledTooltip === 'function' ? b.disabledTooltip(rowId, data) : b.disabledTooltip;
                    return (
                      <div
                        key={`button-${icon}-${idx}`}
                        className="icon-container"
                        data-tooltip-content={disabled && disabledTooltip ? disabledTooltip : text}
                        data-tooltip-id="tooltip"
                      >
                        <div
                          className={`icon ${icon} ${disabled ? 'disabled' : ''} ${highlighted ? 'highlighted' : ''} `}
                          onClick={e => {
                            e.stopPropagation();
                            b.onClick(rowId, data);
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </td>
        )}
        {isLoading ? (
          <td>
            <div className="table-spinner-container">
              <div className="spinner" />
            </div>
          </td>
        ) : null}
      </tr>
    );
  } catch (TypeError) {
    let className;
    if (columns.props.className) {
      if (typeof columns.props.className === 'function') {
        className = d => [columns.key, columns.props.className(d)].join(' ');
      } else {
        className = [columns.key, columns.props.className].join(' ');
      }
    } else {
      className = columns.key;
    }
    return (
      <tr
        className={classNames({
          'table-row': true,
          selected,
        })}
        onClick={() => onClick(rowId)}
      >
        {selection && <SelectionCell selection={selection} rowId={rowId} data={data} />}
        {columns &&
          React.cloneElement(columns, {
            rowId,
            data,
            className,
          })}
        {buttons && buttons.length > 0 && (
          <div className="buttons">
            {buttons.map((b, idx) => (
              <div key={`button-${b.icon}-${idx}`} className="icon-container">
                <div
                  data-tooltip-content={typeof b.text === 'function' ? b.text(rowId, data) : b.text}
                  data-tooltip-id="tooltip"
                  className={`icon ${b.icon} ${
                    (typeof b.disabled === 'function' && b.disabled(rowId, data)) ||
                    (typeof b.disabled == 'boolean' && b.disabled)
                      ? 'disabled'
                      : ''
                  }`}
                  onClick={e => {
                    e.stopPropagation();
                    b.onClick(rowId, data);
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </tr>
    );
  }
};

DataRowTemplate.propTypes = {
  rowId: PropTypes.string,
  data: PropTypes.any,
  selected: PropTypes.any,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  toolTip: PropTypes.any,
  rowClass: PropTypes.string,
  isLoding: PropTypes.bool,
};

export const EmptyRow = ({ colspan, children }) => {
  return (
    <tbody>
      <tr className="empty-row">
        <td colSpan={colspan}>
          <div className="empty-row-container">
            <div className="empty-row-inner" data-testid="no-data-available">
              {children}
            </div>
          </div>
        </td>
      </tr>
    </tbody>
  );
};

EmptyRow.propTypes = {
  children: PropTypes.any,
  colspan: PropTypes.number.isRequired,
};

export const NoDataRow = ({ colspan }) => {
  return <EmptyRow colspan={colspan}>{Strings.noDataAvailable}</EmptyRow>;
};

NoDataRow.propTypes = { colspan: PropTypes.number.isRequired };

export const SpinnerRow = ({ colspan }) => {
  return (
    <EmptyRow colspan={colspan}>
      <div className="spinner" />
    </EmptyRow>
  );
};

SpinnerRow.propTypes = { colspan: PropTypes.number.isRequired };

export const DataLoadingRow = ({ columns, selection }) => (
  <tr className="table-row">
    {selection && <td className="checkbox-column" />}
    {columns &&
      columns.map((c, i) => (
        <td key={`loading-td-${c.name}${i}`}>
          <div className="data-loading" />
        </td>
      ))}
  </tr>
);

DataLoadingRow.propTypes = { columns: PropTypes.array.isRequired, selection: selectionPropType };
